<i18n>
{
	"en": {
		"found": "{count} found"
	},
	"no": {
		"found": "{count} treff"
	}
}
</i18n>
<template>
	<t-form-group :title="title" rowGap="5px">
		<template #header>
			<slot name="header" />
		</template>
		<span class="first-line">
			<t-popover
				:showPopover="shouldShowPopover"
				:marginY="6"
				:style="getStyle"
				position="bottom"
				:min-width="150"
				relative
				v-bind="$attrs"
			>
				<t-text-input
					slot="target"
					ref="input"
					:value="searchValue"
					:placeholder="placeholder"
					:description="description"
					:isInvalid="isInvalid"
					:validationMessage="validationMessage"
					:warningMessage="warningMessage"
					:disabled="disabled"
					relative
					@input="onInput"
					@change="onChange"
					@focus="onFocus"
					@blur="onBlur"
				/>

				<t-menu v-if="filteredItems" style="max-height:200px">
					<t-menu-options-group
						ref="menuOptions"
						v-slot="slotProps"
						:title="showCount ? $tc('found',filteredItems.length,{count: filterTitle}) : ''"
						:items="filteredItems"
						:selectedItems="selectedItems"
						:selectedItem="selectedItem"
						groupStyle="combobox"
						@change="onSelected"
					>
						<slot :item="slotProps.item" :isSelected="slotProps.isSelected" name="optionItem">
							<t-menu-combobox-option-item
								:value="slotProps.item"
								:selected="slotProps.isSelected"
								:focused="slotProps.isFocused"
								height="40px"
							>
								<slot :item="slotProps.item" name="optionItemContent">
									{{displayKey ? slotProps.item[displayKey] : slotProps.item}}
								</slot>
							</t-menu-combobox-option-item>
						</slot>
					</t-menu-options-group>
				</t-menu>
			</t-popover>
		</span>
		<slot name="afterInput" />
	</t-form-group>
</template>

<script>
import inputWrapperMixin from '@/components/evergreen/inputs/inputWrapperMixin';
import formMixin from '@/components/evergreen/inputs/formMixin';
import popoverMixin from '@/components/evergreen/inputs/popoverMixin';

export default {
	name: 'TAdvancedComboBox',
	mixins: [ inputWrapperMixin, formMixin, popoverMixin ],
	props: {
		selectedItems: Array,
		selectedItem: Object,
		title: String,
		displayKey: String,
		items: Array,
		itemFilterFunc: Function,
		limit: Number,
		resetOnSelect: {
			type: Boolean,
			default: false
		},
		showPopoverOnlyOnSearch: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		showCount: {
			type: Boolean,
			default: true
		},
		eagerLoad: Boolean,
	},
	data() {
		return {
			searchValue: '',
			lastSearch: null,
			inputTimeoutId: undefined,
			focused: false
		};
	},
	computed: {
		shouldShowPopover() {
			return this.popoverIsVisible && this.filteredItems.length > 0 && (!this.showPopoverOnlyOnSearch || this.searchValue.length > 0);
		},
		filteredItems() {
			if (this.searchValue === '') return this.items;
			return this.items.filter( item => {
				if (!item || !this.searchValue) {
					return false;
				}
				let searchString = this.searchValue.toLowerCase();

				if (this.itemFilterFunc) return this.itemFilterFunc(item, searchString);
				return this.filterItem(item, searchString);
			});
		},
		filterTitle() {
			return this.limit && this.limit > 0 && this.filteredItems.length >= this.limit ? `${this.limit}+` : this.filteredItems.length;
		}
	},
	watch: {
		selectedItem: {
			immediate: true,
			handler(newVal) {
				if (newVal) {
					this.searchValue = (this.displayKey ? newVal[this.displayKey] : newVal) || '';
					if (this.eagerLoad) this.updateFilteredItems();
				}
			}
		}
	},

	mounted() {
		if (this.selectedItem) {
			this.searchValue = (this.displayKey ? this.selectedItem[this.displayKey] : this.selectedItem) || '';
		}
		if (this.eagerLoad) this.updateFilteredItems();
	},

	methods: {
		onInput(newSearchValue) {
			this.searchValue = newSearchValue;
			this.eventuallyUpdateFilteredItems(true);
			this.$emit('input', newSearchValue);
		},
		eventuallyUpdateFilteredItems(shouldOpenPopover) {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				this.updateFilteredItems();

				// Optimally, the popover should show when items has updated, to prevent drawing popover with old items list.
				// However, the items list may be static. In such case we want to show it immediately.
				// TODO: See if this can be improved
				if (shouldOpenPopover) this.popoverSetVisible();
			},300);
		},
		updateFilteredItems() {
			if (this.items.length < this.limit && this.searchValue.startsWith(this.lastSearch)) return;
			this.$emit('updateFilters', this.searchValue);
			this.lastSearch = this.searchValue;
		},

		filterItem(item, searchString) {
			if (!item) return false;
			if (this.displayKey) item = item[this.displayKey];
			let ret = true;
			for (let c of searchString.split(' ')) {
				ret = ret && (item && item.toLowerCase().includes(c));
			}
			return ret;
		},
		onSelected(value) {
			this.popoverSetHidden();
			this.$emit('select', value);
			if (this.resetOnSelect) this.clearInput();
			this.focused = false;
		},
		onChange(value) {
			if (!value) this.$emit('select', {});
			this.$emit('change', value);
		},
		async onFocus() {
			await this.updateFilteredItems();
			this.focused = true;
		},
		focus() {
			this.$refs.input.focus();
		},
		blur() {
			this.$refs.input.blur();
			this.popoverSetHidden();
			this.focused = false;
		},
		clearInput() {
			this.searchValue = '';
			this.eventuallyUpdateFilteredItems(false);
			this.$emit('input', this.searchValue);
		},
		onBlur(event) {
			if (event.relatedTarget && this.$refs.menuOptions && this.$refs.menuOptions.$el && this.$refs.menuOptions.$el.contains(event.relatedTarget)) {
				event.target.focus();
			} else {
				this.popoverSetHidden();
				this.focused = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.item {
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
}

.item-main-text {
	height:16px;
	line-height: 16px;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
}

.item-sub-text {
	height:16px;
	font-size: 10px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: 1.6;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}

.details {
	@extend %text-small;
	color: get-color($text, 'muted');
	padding-left: 10px;
}

.first-line {
	display: grid;
	column-gap: 20px;
}
</style>
